/**
 * Compte : creation, connexion, profil.
 *
 * Un seul ecran, trois etats, parce que ce sont trois moments du meme parcours
 * et qu'un joueur qui s'est trompe de porte doit pouvoir passer a l'autre sans
 * revenir en arriere.
 *
 * ATTENTION : le socle serveur n'existe pas encore. Les formulaires visent les
 * routes definitives et disent franchement que le service est indisponible
 * plutot que de simuler une session. Le pseudo et l'avatar, eux, sont deja des
 * preferences locales : ils fonctionnent des maintenant, sans compte.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { sanitizeNickname } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { navigate } from '../router.js';
import { Screen } from '../ui/Screen.js';
import { Avatar } from '../ui/Avatar.js';
import { ChevronLeftIcon } from '../ui/icons.js';
import { useUiStore } from '../store/uiStore.js';
import { useAccountStore } from '../account/accountStore.js';
import { ACCOUNT_AVATARS, PAID_AVATARS } from '../account/avatars.js';
import { AccountError, account } from '../net/account.js';
import { PAYMENTS_VISIBLE } from '../payments.js';

type Mode = 'signup' | 'signin';

/** Meme controle que le serveur, pour ne pas lui envoyer une saisie vide. */
function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

export function AccountScreen(): ReactElement {
  const t = useT();
  const status = useAccountStore((state) => state.status);
  const profile = useAccountStore((state) => state.profile);
  const setProfile = useAccountStore((state) => state.setProfile);
  // Jeton de reinitialisation, apporte par le lien du courriel.
  const reset = new URLSearchParams(window.location.search).get('reset');

  // Retour de Stripe : `?bought=` porte l'identifiant de la session de
  // paiement. Il ne prouve rien par lui-meme (n'importe qui peut l'ecrire dans
  // sa barre d'adresse), c'est le serveur qui le fera trancher par Stripe.
  const bought = new URLSearchParams(window.location.search).get('bought');

  /**
   * Le cookie de session fait foi : on demande au serveur qui l'on est plutot
   * que de croire un etat local, qui pourrait survivre a une deconnexion.
   *
   * AU RETOUR DE STRIPE, on passe par `confirm`, pas par `me`. Le navigateur
   * revient ici des que le formulaire est valide, alors que le compte n'est
   * credite qu'a l'arrivee du webhook, sur une AUTRE connexion, quand Stripe le
   * decide : la course se perd regulierement, et l'ecran affichait l'etat
   * d'avant l'achat sans plus rien qui bouge. `confirm` supprime la course au
   * lieu de l'attendre, en faisant verifier la session par Stripe sur-le-champ,
   * et rend le profil deja credite. Aucun rechargement de page, aucune attente.
   *
   * `me` reste le premier appel : il faut savoir qui l'on est meme si `confirm`
   * echoue, et c'est le seul chemin quand on arrive ici sans acheter.
   */
  useEffect(() => {
    let cancelled = false;

    account
      .me()
      .then((next) => {
        if (cancelled) return;
        setProfile(next);
        // Un compte deja paye n'a rien a confirmer : c'est le cas d'un simple
        // rechargement de l'URL de retour.
        if (bought === null || next === null || next.paid) return;
        return account.confirm(bought).then((confirmed) => {
          if (!cancelled) setProfile(confirmed);
        });
      })
      .catch(() => undefined);

    // Le formulaire ne demande plus de pseudo : on s'assure qu'il en existe un,
    // sans ecraser celui que le joueur a choisi sur l'accueil.
    useUiStore.getState().ensureNickname();
    return () => {
      cancelled = true;
    };
  }, [setProfile, bought]);

  return (
    <Screen name="account" scroll>
      <main className="account">
        <div className="lobby__nav">
          <button type="button" className="button-back" onClick={() => navigate('/')}>
            <ChevronLeftIcon />
            {t('common.back')}
          </button>
        </div>

        {reset !== null ? (
          <ResetForm token={reset} />
        ) : status === 'signedIn' && profile !== null ? (
          <Profile />
        ) : (
          <AuthForms />
        )}
      </main>
    </Screen>
  );
}

/**
 * Le compte fait autorite sur le pseudo et l'avatar.
 *
 * Sans cette synchronisation, se connecter sur un second appareil garderait les
 * preferences locales de CET appareil, et le compte ne servirait a rien.
 */
function useSyncedProfile(): void {
  const profile = useAccountStore((state) => state.profile);
  useEffect(() => {
    if (profile === null) return;
    const ui = useUiStore.getState();
    if (profile.nickname !== null) ui.setNickname(profile.nickname);
    if (profile.avatar !== null) ui.setAvatar(profile.avatar);
  }, [profile]);
}

/** Pseudo et avatar : les deux reglages qui suivent le joueur d'une partie a l'autre. */
function Identity(): ReactElement {
  const t = useT();
  const signedIn = useAccountStore((state) => state.status) === 'signedIn';
  // `false` seulement pour un compte non confirme : un visiteur anonyme voit
  // les avatars payants et peut cliquer, c'est ainsi qu'il decouvre l'achat.
  const verified = useAccountStore((state) =>
    state.profile === null ? null : state.profile.emailVerified,
  );
  // L'achat est fait : les avatars payants deviennent des choix comme les
  // autres. Sans ce drapeau, un compte debloque continuait d'etre renvoye vers
  // la page de paiement au clic, ce qui se lit comme un achat perdu.
  const paid = useAccountStore((state) => state.profile?.paid === true);
  const nickname = useUiStore((state) => state.nickname);
  const setNickname = useUiStore((state) => state.setNickname);
  const avatar = useUiStore((state) => state.avatar);
  const setAvatar = useUiStore((state) => state.setAvatar);

  /** Un changement part au serveur DES qu'il est fait, sans bouton de plus. */
  const push = (nextNickname: string, nextAvatar: string | null): void => {
    if (!signedIn) return;
    void account.profile(nextNickname, nextAvatar).catch(() => undefined);
  };

  return (
    <section className="account__block">
      <label className="field">
        <span className="field__label">{t('home.nickname')}</span>
        <input
          className="field__input"
          value={nickname}
          maxLength={16}
          placeholder={t('home.nicknamePlaceholder')}
          onChange={(event) => setNickname(event.target.value)}
          onBlur={(event) => {
            const clean = sanitizeNickname(event.target.value) ?? event.target.value;
            setNickname(clean);
            push(clean, avatar);
          }}
        />
      </label>

      <p className="section-label">{t('account.avatar')}</p>
      {/* Sans compte, on ne choisit pas : l'avatar est tire au hasard et c'est
          precisement ce qu'un compte vient lever. On montre donc ce qu'il
          ouvre, plutot qu'une grille grisee sans explication. */}
      {!signedIn && <p className="hint">{t('account.avatarLocked')}</p>}

      <div className="account__avatars" role="radiogroup" aria-label={t('account.avatar')}>
        {ACCOUNT_AVATARS.map((choice) => (
          <button
            key={choice.id}
            type="button"
            role="radio"
            aria-checked={avatar === choice.id}
            disabled={!signedIn}
            className="account__avatar"
            onClick={() => {
              setAvatar(choice.id);
              push(nickname, choice.id);
            }}
          >
            <Avatar playerId={choice.id} colorIndex={0} size={56} avatar={choice.id} />
          </button>
        ))}
      </div>

      <PaidAvatars
        paid={paid}
        verified={verified}
        current={avatar}
        onPick={(id) => {
          setAvatar(id);
          push(nickname, id);
        }}
      />
    </section>
  );
}

/**
 * Les avatars payants, ou rien du tout.
 *
 * RIEN DU TOUT est le cas courant aujourd'hui : plus personne n'achete (cf.
 * payments.ts). Un compte DEJA paye les garde pourtant, et ce n'est pas une
 * exception de complaisance : ce n'est plus une vitrine pour lui, c'est ce
 * qu'il possede, le masquer lui retirerait ce qu'il a paye.
 *
 * Deux grilles plutot qu'une, et la difference est de fond : payant, c'est un
 * `radiogroup` comme celui des avatars de compte ; sinon, une rangee de portes
 * vers l'achat. Les fondre demanderait de changer le role ARIA au vol, ce
 * qu'aucun lecteur d'ecran ne suit.
 */
function PaidAvatars({
  paid,
  verified,
  current,
  onPick,
}: {
  readonly paid: boolean;
  readonly verified: boolean | null;
  readonly current: string | null;
  readonly onPick: (id: string) => void;
}): ReactElement | null {
  const t = useT();
  if (!PAYMENTS_VISIBLE && !paid) return null;

  return (
    <>
      <p className="section-label">{t('account.avatarPaid')}</p>
      {!paid && verified === false && <p className="hint">{t('account.verifyFirst')}</p>}
      {paid ? (
        <div className="account__avatars" role="radiogroup" aria-label={t('account.avatarPaid')}>
          {PAID_AVATARS.map((choice) => (
            <button
              key={choice.id}
              type="button"
              role="radio"
              aria-checked={current === choice.id}
              className="account__avatar"
              onClick={() => onPick(choice.id)}
            >
              <Avatar playerId={choice.id} colorIndex={0} size={56} avatar={choice.id} />
            </button>
          ))}
        </div>
      ) : (
        <div className="account__avatars">
          {PAID_AVATARS.map((choice) => (
            <button
              key={choice.id}
              type="button"
              className="account__avatar"
              aria-label={t('account.avatarBuy')}
              title={t('account.avatarBuy')}
              // Jamais de selection silencieuse : un avatar payant mene a la
              // page d'achat, et nulle part ailleurs.
              disabled={verified === false}
              onClick={() => navigate('/purchase')}
            >
              <Avatar playerId={choice.id} colorIndex={0} size={56} avatar={choice.id} />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Connecte : le profil, le pseudo, l'avatar, et l'acces a l'achat. */
function Profile(): ReactElement {
  const t = useT();
  useSyncedProfile();
  const profile = useAccountStore((state) => state.profile);
  const setProfile = useAccountStore((state) => state.setProfile);
  const avatar = useUiStore((state) => state.avatar);

  const signOut = useCallback(() => {
    void account
      .signOut()
      .catch(() => undefined)
      .finally(() => setProfile(null));
  }, [setProfile]);

  return (
    <>
      <header className="account__header">
        <Avatar playerId={profile?.id ?? 'me'} colorIndex={0} size={72} avatar={avatar} />
        <h1 className="account__title">{t('account.title')}</h1>
        <p className="account__email">{profile?.email}</p>
        {profile?.emailVerified === false && <p className="hint">{t('account.verifyPending')}</p>}
      </header>

      <Identity />

      <footer className="account__footer">
        {/* Un compte debloque ne se voit proposer NI l'achat NI un bouton grise :
            il a paye, l'ecran doit le dire, sinon le paiement n'a laisse aucune
            trace visible et se lit comme perdu. */}
        {profile?.paid === true && <p className="account__owned">{t('purchase.owned')}</p>}
        {/* La porte vers l'achat, fermee tant que rien ne se vend. Un bouton
            grise aurait ete pire que son absence : il annonce un privilege et
            refuse de l'ouvrir, sans dire quand (cf. payments.ts). */}
        {PAYMENTS_VISIBLE && profile?.paid !== true && (
          <>
            {/* L'achat est le SEUL endroit ou l'adresse compte vraiment : elle
                portera le recu et rattachera le paiement au compte. C'est donc
                la qu'on exige la confirmation, et pas a l'entree du jeu. */}
            <button
              type="button"
              className="button-primary button-block"
              disabled={profile?.emailVerified !== true}
              onClick={() => navigate('/purchase')}
            >
              {t('account.purchase')}
            </button>
            {profile?.emailVerified !== true && <p className="hint">{t('account.verifyFirst')}</p>}
          </>
        )}
        <button type="button" className="button-ghost button-block" onClick={signOut}>
          {t('account.signOut')}
        </button>
      </footer>
    </>
  );
}

/** Nouveau mot de passe, apres avoir suivi le lien recu par courriel. */
function ResetForm({ token }: { token: string }): ReactElement {
  const t = useT();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const setProfile = useAccountStore((state) => state.setProfile);

  return (
    <section className="account__block">
      <h1 className="account__title">{t('account.resetTitle')}</h1>
      <label className="field">
        <span className="field__label">{t('account.newPassword')}</span>
        <input
          className="field__input"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>
      {error !== null && <p className="account__error">{error}</p>}
      <button
        type="button"
        className="button-primary button-block"
        disabled={password.length < 8}
        onClick={() => {
          void account
            .reset(token, password)
            .then((profile) => {
              setProfile(profile);
              // L'URL garde sinon un jeton deja consomme, qu'un rechargement
              // reessaierait pour rien.
              navigate('/account', { replace: true });
            })
            .catch(() => setError(t('account.failed')));
        }}
      >
        {t('account.signIn')}
      </button>
    </section>
  );
}

/** Anonyme : creation ou connexion, avec Google en premier. */
function AuthForms(): ReactElement {
  const t = useT();
  const [mode, setMode] = useState<Mode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setProfile = useAccountStore((state) => state.setProfile);

  const submit = useCallback(() => {
    setBusy(true);
    setError(null);
    const call = mode === 'signup' ? account.create : account.signIn;
    void call(email.trim(), password)
      .then(setProfile)
      .catch((cause: unknown) => {
        // Le service n'existe pas encore : on le DIT, plutot que d'afficher une
        // erreur de saisie qui enverrait le joueur chercher sa faute.
        setError(
          cause instanceof AccountError && cause.message === 'unavailable'
            ? t('account.unavailable')
            : t('account.failed'),
        );
      })
      .finally(() => setBusy(false));
  }, [mode, email, password, setProfile, t]);

  return (
    <>
      <header className="account__header">
        <h1 className="account__title">
          {mode === 'signup' ? t('account.createTitle') : t('account.signInTitle')}
        </h1>
        {/* Deux phrases pour la meme promesse : celle qui mentionne les modes
            payants ne s'affiche que si quelque chose se vend. Annoncer un
            privilege qu'on ne peut pas acheter fait renoncer a ce qui est
            pourtant ouvert (cf. payments.ts). */}
        <p className="hint">{t(PAYMENTS_VISIBLE ? 'account.why' : 'account.whyFree')}</p>
      </header>

      <section className="account__block">
        <button type="button" className="button-ghost button-block" onClick={() => account.google()}>
          {t('account.google')}
        </button>

        <p className="account__or">{t('account.or')}</p>

        <label className="field">
          <span className="field__label">{t('account.email')}</span>
          <input
            className="field__input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('account.password')}</span>
          <input
            className="field__input"
            type="password"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {error !== null && <p className="account__error">{error}</p>}

        <button
          type="button"
          className="button-primary button-block"
          disabled={busy || email.trim() === '' || password === ''}
          onClick={submit}
        >
          {mode === 'signup' ? t('account.create') : t('account.signIn')}
        </button>

        {mode === 'signin' && (
          <button
            type="button"
            className="button-ghost button-block"
            onClick={() => {
              if (!isEmail(email)) return setError(t('account.failed'));
              void account.forgot(email.trim()).catch(() => undefined);
              setError(t('account.forgotSent'));
            }}
          >
            {t('account.forgot')}
          </button>
        )}

        {/* L'autre porte, toujours visible : celui qui a deja un compte ne doit
            pas avoir a revenir en arriere pour le comprendre. */}
        <button
          type="button"
          className="button-ghost button-block"
          onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
        >
          {mode === 'signup' ? t('account.signIn') : t('account.create')}
        </button>
      </section>
    </>
  );
}
